import api from './api';
import { UserRole, NavigationModule, PermissionType } from '@/types/enums';

export interface RBACPermission {
  module: NavigationModule;
  permissions: PermissionType[];
}

export interface RoleDefinition {
  id: string; // Required for CRUDService interface
  role: UserRole | string; // Can be system role or custom role name
  name: string;
  description: string;
  permissions: RBACPermission[];
  isSystemRole: boolean;
  canBeModified: boolean;
  tenantId?: string;
  isActive?: boolean;
  [key: string]: unknown; // Index signature for DataItem compatibility
}

export interface AccessCheckResult {
  hasAccess: boolean;
  userRole: UserRole;
  module: NavigationModule;
  permission: PermissionType | null;
}

// ============================================================================
// TENANT ADMINISTRATOR ROLE MANAGEMENT INTERFACES
// ============================================================================

export interface TenantAdministrator {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  userStatus: string;
  userCreatedAt: string;
  tenantId: string;
  tenantName: string;
  tenantDomain: string;
  tenantStatus: string;
  roleAssignmentId: string;
  roleStatus: string;
  roleAssignedAt: string;
}

export interface RolePermission {
  id: string;
  resourceType: string;
  action: string;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RoleAssignment {
  assignmentId: string;
  userId: string;
  userEmail: string;
  userName: string;
  tenantId: string;
  tenantName: string;
  roleType: string;
  assignedAt?: string;
  revokedAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  count?: number;
  error?: string;
}

export const rbacService = {
  /**
   * Get all role definitions
   */
  async getRoles(): Promise<RoleDefinition[]> {
    const response = await api.get('/rbac/roles');
    return response.data.data;
  },

  /**
   * Get specific role definition
   */
  async getRole(role: UserRole | string): Promise<RoleDefinition> {
    const response = await api.get(`/rbac/roles/${role}`);
    return response.data.data;
  },

  /**
   * Update role permissions (Platform Admin only)
   */
  async updateRolePermissions(role: UserRole | string, permissions: RBACPermission[]): Promise<void> {
    await api.put(`/rbac/roles/${role}/permissions`, { permissions });
  },

  /**
   * Get accessible modules for current user
   */
  async getUserModules(): Promise<NavigationModule[]> {
    const response = await api.get('/rbac/user/modules');
    return response.data.data;
  },

  /**
   * Check if user has access to specific module/permission
   */
  async checkAccess(module: NavigationModule, permission?: PermissionType): Promise<AccessCheckResult> {
    const response = await api.post('/rbac/check-access', { module, permission });
    return response.data.data;
  },

  /**
   * Get all available modules
   */
  async getModules(): Promise<NavigationModule[]> {
    const response = await api.get('/rbac/modules');
    return response.data.data;
  },

  /**
   * Get all available permission types
   */
  async getPermissions(): Promise<PermissionType[]> {
    const response = await api.get('/rbac/permissions');
    return response.data.data;
  },

  async createRole(roleData: {
    roleName: string;
    displayName: string;
    description: string;
    permissions: RBACPermission[];
  }): Promise<RoleDefinition> {
    const response = await api.post('/rbac/roles', roleData);
    return response.data.data;
  },

  async updateRole(roleName: string, roleData: {
    displayName: string;
    description: string;
    permissions: RBACPermission[];
  }): Promise<RoleDefinition> {
    const response = await api.put(`/rbac/roles/${roleName}`, roleData);
    return response.data.data;
  },

  async deleteRole(roleName: string): Promise<boolean> {
    const response = await api.delete(`/rbac/roles/${roleName}`);
    return response.data.success;
  },

  // ============================================================================
  // TENANT ADMINISTRATOR ROLE MANAGEMENT (PLATFORM ADMIN ONLY)
  // ============================================================================

  /**
   * Get all tenant administrators across all tenants
   */
  async getTenantAdministrators(): Promise<ApiResponse<TenantAdministrator[]>> {
    try {
      const response = await api.get('/rbac/tenant-administrators');
      return response.data;
    } catch (error) {
      console.error('Error getting tenant administrators:', error);
      throw error;
    }
  },

  /**
   * Get tenant administrator role permissions
   */
  async getTenantAdminRolePermissions(): Promise<ApiResponse<RolePermission[]>> {
    try {
      const response = await api.get('/rbac/tenant-administrators/permissions');
      return response.data;
    } catch (error) {
      console.error('Error getting tenant admin role permissions:', error);
      throw error;
    }
  },

  /**
   * Update tenant administrator role permissions
   */
  async updateTenantAdminRolePermissions(permissions: RolePermission[]): Promise<ApiResponse<RolePermission[]>> {
    try {
      const response = await api.put('/rbac/tenant-administrators/permissions', {
        permissions
      });
      return response.data;
    } catch (error) {
      console.error('Error updating tenant admin role permissions:', error);
      throw error;
    }
  },

  /**
   * Assign tenant administrator role to user
   */
  async assignTenantAdminRole(targetUserId: string, tenantId: string): Promise<ApiResponse<RoleAssignment>> {
    try {
      const response = await api.post('/rbac/tenant-administrators/assign', {
        targetUserId,
        tenantId
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning tenant admin role:', error);
      throw error;
    }
  },

  /**
   * Revoke tenant administrator role from user
   */
  async revokeTenantAdminRole(targetUserId: string, tenantId: string): Promise<ApiResponse<RoleAssignment>> {
    try {
      const response = await api.post('/rbac/tenant-administrators/revoke', {
        targetUserId,
        tenantId
      });
      return response.data;
    } catch (error) {
      console.error('Error revoking tenant admin role:', error);
      throw error;
    }
  }
};
